import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";
import { authStorageService } from "./auth-storage.service";

import type {
  CheckAvailability,
  Supplier,
  CreateSupplierRequest,
  SupplierFilter,
  SupplierResponse,
  SupplierDetailResponse as SupplierByCodeResponse,
} from "../types/suppliers.types";

type SupplierDetailResponse = {
  success: boolean;
  statusCode: number;
  message: string;
  resData: { supplier: Supplier } | Supplier;
};

type SupplierMutationResponse = {
  success: boolean;
  statusCode: number;
  message: string;
  resData: unknown;
};

// password is required by the backend on create only
type SupplierCreatePayload = CreateSupplierRequest & { password: string };

// create is always "active"; updates may move a supplier to any status
type SupplierUpdatePayload = Partial<
  Omit<CreateSupplierRequest, "status" | "inventory_status">
> & {
  status?: Supplier["status"];
  inventory_status?: string;
};

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.suppliers;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const suppliersService = {
  async list(filter: SupplierFilter): Promise<SupplierResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<SupplierResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load suppliers due to an unexpected error.",
      ) as unknown as SupplierResponse;
    }
  },

  async details(id: number | string): Promise<SupplierDetailResponse> {
    try {
      return await apiClient<SupplierDetailResponse>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load supplier details due to an unexpected error.",
      ) as unknown as SupplierDetailResponse;
    }
  },

  async detailsByCode(supplierCode: string): Promise<SupplierByCodeResponse> {
    try {
      return await apiClient<SupplierByCodeResponse>(
        endpoints.supplierByCode(encodeURIComponent(supplierCode)),
        {
          method: "GET",
          isAuthenticated: true,
          cache: "no-store",
        },
      );
    } catch (error) {
      return failure(
        error,
        "Failed to load supplier details due to an unexpected error.",
      ) as unknown as SupplierByCodeResponse;
    }
  },

  async create(payload: SupplierCreatePayload): Promise<SupplierMutationResponse> {
    try {
      return await apiClient<SupplierMutationResponse>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to create supplier due to an unexpected error.",
      ) as SupplierMutationResponse;
    }
  },

  async update(
    id: number | string,
    payload: SupplierUpdatePayload,
  ): Promise<SupplierMutationResponse> {
    try {
      return await apiClient<SupplierMutationResponse>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to update supplier due to an unexpected error.",
      ) as SupplierMutationResponse;
    }
  },

  async updateStatus(
    id: number | string,
    payload: { status: Supplier["status"] },
  ): Promise<SupplierMutationResponse> {
    return suppliersService.update(id, payload);
  },

  // Also used by the public signup page, so the token is sent only when present
  async checkAvailability(
    filter: Partial<CheckAvailability>,
  ): Promise<SupplierMutationResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value) params.set(key, String(value));
      });

      return await apiClient<SupplierMutationResponse>(
        endpoints.checkAvailability(params.toString()),
        {
          method: "GET",
          isAuthenticated: !!authStorageService.getToken(),
          cache: "no-store",
        },
      );
    } catch (error) {
      return failure(
        error,
        "Failed to check availability due to an unexpected error.",
      ) as SupplierMutationResponse;
    }
  },

  async delete(id: number | string): Promise<SupplierMutationResponse> {
    try {
      return await apiClient<SupplierMutationResponse>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to delete supplier due to an unexpected error.",
      ) as SupplierMutationResponse;
    }
  },
};
